import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { getAccountStructure, TFPageContent } from '@tf/shared';
import { Button, Flex, Stack, Stepper, TFCard, TFText, Title, useDisclosureWithParams } from '@tf/ui';
import { T } from '@tf/utils';
import { useEffect, useState } from 'react';
import { useApplication, useApplicationMainReview } from '@/components/application';
import {
	getApplicantContext,
	hideApplicantKindConnections,
} from '@/components/application/hideApplicantKindConnections';
import { MarkdownPageIntro, useGetConnectionLabels, useMenu } from '@/components/shared';
import { TFStepper } from '@/components/shared/TFStepper';
import { AddConnectionModal, type AddConnectionModalProps } from '@/components/structure/AddConnectionModal';
import { AddEntityStep } from '@/components/structure/AddEntityStep';
import { AddOtherConnections } from '@/components/structure/AddOtherConnections';
import { FormModal } from '@/components/structure/FormModal';
import { type EntityFormsContext, getEntityForms } from '@/components/structure/getEntityForms';
import { getReviewConnections } from '@/components/structure/getReviewConnections';
import { ModalsControllerProvider } from '@/components/structure/ModalsControllerContext';
import { RemoveConnectionModal, type RemoveConnectionModalProps } from '@/components/structure/RemoveConnectionModal';
import { ReviewStructureStep } from '@/components/structure/ReviewStructureStep';
import { useStructureEntities } from '@/components/structure/useStructureEntities';
import { useDefinitions } from '@/core/providers';
import { useApplicationStructureParams } from '@/routes/endClientArea/params';

export const GraphStructure = () => {
	const { defs } = useDefinitions();
	const { _ } = useLingui();
	const addConnectionModal = useDisclosureWithParams<AddConnectionModalProps>();
	const formModal = useDisclosureWithParams<EntityFormsContext>();
	const connectionsMenu = useDisclosureWithParams<{ entityId?: number }>();
	const removeConnectionModal = useDisclosureWithParams<RemoveConnectionModalProps>();
	const application = useApplication();

	const { menuItems } = useMenu();
	const formIndex = menuItems.findIndex((item) => item.isSelected);
	const currentMenuItem = menuItems.find((item) => item.isSelected);

	const { applicationConnectionKind } = useApplicationStructureParams();
	const mainReview = useApplicationMainReview();

	const entities = useStructureEntities(applicationConnectionKind);

	const mainEntity = entities.find((e) =>
		applicationConnectionKind === 'APPLICANT'
			? e.entityId === mainReview.graphNodeId
			: e.entityId === application.account.graphNodeId,
	);
	T.assertNotNullish(mainEntity, 'no entity found');

	const sortedConnections = mainEntity.connections.sort((a, b) => {
		if (a.isRequired !== b.isRequired) {
			return a.isRequired ? -1 : 1;
		}

		return a.connectionKind.localeCompare(b.connectionKind);
	});
	const requiredConnectionsCount = sortedConnections.filter((c) => c.isRequired).length;
	const slicedConnections =
		sortedConnections.length < 4
			? sortedConnections
			: [
					...sortedConnections.slice(0, requiredConnectionsCount),
					{ connectedEntities: [], connectionKind: 'OTHERS', isRequired: false },
				];

	const initialStep = slicedConnections.findIndex((connection) => !connection.connectedEntities.length);
	const [step, setStep] = useState<number>(initialStep === -1 ? slicedConnections.length : initialStep);
	const stepsAmount = slicedConnections.length + 1;
	const showOnlyReview = requiredConnectionsCount === 0;
	const isOnReviewStep = step === slicedConnections.length;
	const contentMaxWidth = showOnlyReview || isOnReviewStep ? '1100px' : '800px';

	const entitiesForms = formModal.params ? getEntityForms(entities, formModal.params)?.map((f) => f.form) : undefined;

	const entityToEdit = entities.find((e) => e.entityId === formModal.params?.entityId);

	const accountStructure = hideApplicantKindConnections(
		getAccountStructure({
			account: {
				meta: application.account,
				// Show subgraph for current connection type
				reviews: getReviewConnections(application, applicationConnectionKind),
			},
			showRootNode: applicationConnectionKind !== 'APPLICANT',
			entitiesDefs: defs.entities,
		}),
		getApplicantContext(
			mainReview,
			defs.accounts[application.account.entityKind].connection_filters,
			application.account.graphNodeId,
		),
	);

	const connectedEntities =
		slicedConnections[step]?.connectedEntities.map((connection) => ({
			...connection,
			connectionKind: slicedConnections[step].connectionKind,
		})) ?? [];

	const connectionDescription =
		defs.accounts[application.account.entityKind].connections?.[applicationConnectionKind]?.description;
	const getConnectionLabels = useGetConnectionLabels(
		addConnectionModal.params?.parentEntityKind ?? mainEntity.entityKind,
	);

	const connectionLabel = getConnectionLabels(addConnectionModal.params?.connectionKind ?? '').singular;

	useEffect(() => {
		setStep(0);
	}, []);

	return (
		<ModalsControllerProvider value={{ addConnectionModal, formModal, removeConnectionModal, connectionsMenu }}>
			<Stack w="100%" maw={contentMaxWidth}>
				<TFPageContent m="0">
					<Stack align="center" gap="16px">
						<Title mt="16px" fz="32px" lh="40px" fw="600">
							{currentMenuItem?.label}
						</Title>
						{connectionDescription && <MarkdownPageIntro text={connectionDescription} />}
						{!showOnlyReview ? (
							<>
								<TFStepper
									stepsCount={slicedConnections.length + 1}
									step={step}
									onStepChange={setStep}
									maxScrollAreaWidth={800}
									allowNextStepsSelect
								>
									{slicedConnections.map(({ connectionKind }) => {
										if (connectionKind === 'OTHERS') {
											return (
												<Stepper.Step
													key={connectionKind}
													label={_(
														msg({
															id: 'structure.stepper.otherStepLabel',
															message: `Other`,
														}),
													)}
												/>
											);
										}

										return <Stepper.Step key={connectionKind} label={getConnectionLabels(connectionKind)?.singular} />;
									})}
									<Stepper.Step
										label={_(
											msg({
												id: 'structure.stepper.reviewStepLabel',
												message: `Structure review`,
											}),
										)}
									/>
								</TFStepper>

								{slicedConnections[step]?.connectionKind === 'OTHERS' ? (
									<AddOtherConnections
										entities={entities}
										mainEntity={mainEntity}
										otherConnections={sortedConnections.slice(requiredConnectionsCount)}
									/>
								) : step !== slicedConnections.length ? (
									<AddEntityStep
										entities={entities}
										connectedEntities={connectedEntities}
										mainEntity={mainEntity}
										connectionKind={sortedConnections[step]?.connectionKind}
									/>
								) : (
									<ReviewStructureStep accountStructure={accountStructure}>
										<TFText mb="12px" fz="14px" lh="18px" fw="500">
											<Trans id="structure.reviewStructureStep.label">
												Review the structure and ensure all relevant stakeholders are included.
											</Trans>
										</TFText>
									</ReviewStructureStep>
								)}
							</>
						) : (
							<ReviewStructureStep accountStructure={accountStructure} />
						)}
					</Stack>
				</TFPageContent>

				<FormModal
					fullScreen
					title={
						_(
							msg({
								id: 'flatStructure.formModal.title',
								message: `Provide information about`,
							}),
						) + entityToEdit?.entityName
					}
					opened={Boolean(formModal.isOpen && entitiesForms)}
					onClose={formModal.close}
					contentProps={{
						forms: entitiesForms ?? [],
						onClose: formModal.close,
					}}
				/>

				<AddConnectionModal
					size="500px"
					opened={addConnectionModal.isOpen}
					onClose={addConnectionModal.close}
					// TODO:
					title={`${_(
						msg({
							id: 'flatStructure.addConnectionModal.title',
							message: `Add ${connectionLabel} for `,
						}),
					)} ${addConnectionModal.params?.parentEntityName}`}
					contentProps={
						addConnectionModal.params
							? {
									...addConnectionModal.params,
									onConnectionAdded: (id) => {
										formModal.open({
											entityId: id,
											parentEntityId: addConnectionModal.params?.parentEntityId,
											connectionKind: addConnectionModal.params?.connectionKind,
										});
									},
								}
							: null
					}
				/>

				<RemoveConnectionModal
					size="600px"
					opened={removeConnectionModal.isOpen}
					onClose={removeConnectionModal.close}
					title={_(
						msg({
							id: 'structure.removeConnectionModal.title.connection',
							message: 'Remove the connection?',
						}),
					)}
					contentProps={removeConnectionModal.params}
				/>

				<TFCard maw={contentMaxWidth} innerPadding="12px">
					<Flex direction="row-reverse">
						<Button
							mih="36px"
							onClick={() => {
								setStep((prev) => {
									if (prev < stepsAmount - 1) {
										return prev + 1;
									} else {
										const nextForm = menuItems[formIndex + 1];
										nextForm.onClick();
										return prev;
									}
								});
							}}
						>
							{_(
								msg({
									id: 'structure.graphStructure.saveButton.label',
									message: `Save and continue`,
								}),
							)}
						</Button>
					</Flex>
				</TFCard>
			</Stack>
		</ModalsControllerProvider>
	);
};
